import { useQuery } from "@tanstack/react-query";
import { getCapabilities } from "@/features/capabilities/api/capabilitiesApi";
import type { CapabilitiesDto, CapabilityModuleKey, ModuleCapability } from "@/features/capabilities/types";

/**
 * Fallback i sigurt kur API /capabilities dështon (gabim rrjeti ose 5xx).
 * Parimi Zero-Trust: modulet sensitive shfaqen si të çaktivizuara.
 * Backend mbetet autoriteti i vërtetë i sigurisë.
 */
const DISABLED: ModuleCapability = { enabled: false, status: "inactive", reason: "Gabim rrjeti — riprovo." };

const SAFE_FALLBACK: CapabilitiesDto = {
  institution_level: 1,
  modules: {
    monitoring:   DISABLED,
    family_care:  DISABLED,
    wallet:       DISABLED,
    appointments: DISABLED,
    admin:        DISABLED,
    superadmin:   DISABLED,
    laboratory:    DISABLED,
    inventory:     DISABLED,
    finance:       DISABLED,
    analytics:     DISABLED,
    hospitalization: DISABLED,
    chat:          DISABLED,
  } satisfies Record<CapabilityModuleKey, ModuleCapability>,
};

/**
 * Lexon gjendjen e moduleve nga backend.
 * - Nëse API kthen sukses → përdor të dhënat reale.
 * - Nëse API dështon → SAFE_FALLBACK (të gjithë disabled).
 * - Vetëm për UX; backend mbetet autoriteti i lejeve.
 */
export const useCapabilities = () =>
  useQuery({
    queryKey: ["capabilities"],
    queryFn: getCapabilities,
    retry: 1,
    // Kur query-ja ka error, kthen fallback-un e sigurt
    select: (data) => data,
    placeholderData: SAFE_FALLBACK,
    // Aprovimi nga Superadmini ndodh ne nje sesion tjeter. Rifreskimi i
    // capabilities e bën nav-in e biznesit te shfaqe modulin sapo aktivizohet.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

/**
 * Kthen SAFE_FALLBACK nëse query-ja ka error ose është duke u ngarkuar.
 * Garanton që kodi i thirrësit gjithmonë merr një CapabilitiesDto të vlefshëm.
 */
export const useCapabilitiesSafe = (): CapabilitiesDto => {
  const { data, isError } = useCapabilities();
  if (isError || !data) return SAFE_FALLBACK;
  return data;
};
